import { executeWithRetry, isTransientError, RetryConfig, TransientError, fetchWithNetworkHandling, checkResponseOk } from "./NetworkUtils";

export type UploadRequest = {
  uploadId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  metadata: Record<string, string>;
};

export type UploadSession = {
  fileUrl: string;
  fileId: string;
}

export async function StartFileUpload(
  baseUrl: string,
  request: UploadRequest,
  retryConfig?: RetryConfig
) {
  return executeWithRetry(async () => {
    const uploadUrl = `${baseUrl}/files`;
    const response = await fetchWithNetworkHandling(uploadUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });

    checkResponseOk(response, "File upload failed");

    const body = (await response.json()) as UploadSession;
    if (!body.fileUrl || !body.fileId) {
      throw new Error("Invalid response from server");
    }

    return body;
  }, "StartFileUpload", retryConfig);
}

export async function UploadBlock(
  fileUrl: string,
  offset: number,
  chunk: Blob,
  retryConfig?: RetryConfig
) {
  return executeWithRetry(async () => {
    const blockId = offset.toString().padStart(12, "0");
    const blockIdBase64 = btoa(blockId);
    const blockUrl = `${fileUrl}&comp=block&blockid=${encodeURIComponent(
      blockIdBase64
    )}`;

    const response = await fetchWithNetworkHandling(blockUrl, {
      method: "PUT",
      headers: {
        "Content-Length": chunk.size.toString(),
        "Content-Type": "application/octet-stream",
      },
      body: chunk,
    });

    checkResponseOk(response, "Block upload failed");
  }, `UploadBlock(offset=${offset})`, retryConfig);
}

export async function CommitBlocks(
  baseUrl: string,
  fileId: string,
  retryConfig?: RetryConfig
) {
  return executeWithRetry(async () => {
    const commitUrl = `${baseUrl}/files/${fileId}/committed`;
    const response = await fetchWithNetworkHandling(commitUrl, {
      method: "PUT",
    });

    checkResponseOk(response, "Commit blocks failed");
  }, "CommitBlocks", retryConfig);
}

